/** @format */

import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, Link as RouterLink, useParams } from "react-router-dom";
import { AppDispatch, RootState } from "../../../redux/store/store";
import {
	Box,
	Typography,
	CircularProgress,
	Link,
	IconButton,
	Tooltip,
	Snackbar,
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import BusinessIcon from "@mui/icons-material/Business";
import { selectAccountLink } from "../../../redux/selectors/accountVerification";
import { createAccountLink } from "../../../redux/actions/circleAction";
import { selectCurrentUser } from "../../../redux/selectors/userSelector";
import { Alert, Link as MUILink } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

export default function AccountLink() {
	const { userId } = useParams<{ userId: string }>();
	const userData = useSelector(selectCurrentUser);
	const navigate = useNavigate();
	const dispatch = useDispatch<AppDispatch>();
	const COMPANY_URL = "https://fundsyfi.com";

	const [copied, setCopied] = useState(false);

	const { linkUrl, isLoading, error } = useSelector((state: RootState) =>
		selectAccountLink(state),
	);
	const [countdown, setCountdown] = useState(5);

	// 1. Fire the thunk on mount
	useEffect(() => {
		console.log("no userId redirecting to home");
		if (!userId) {
			return navigate(`/`, { replace: true });
		}
		dispatch(createAccountLink(userId.toString()));
	}, [dispatch, navigate, userId]);

	// 2. When linkUrl is set, immediately redirect
	useEffect(() => {
		if (linkUrl) {
			window.location.assign(linkUrl);
			// fallback countdown
			let sec = 5;
			const timer = setInterval(() => {
				sec -= 1;
				setCountdown(sec);
				if (sec <= 0) {
					clearInterval(timer);
					window.location.assign(linkUrl);
				}
			}, 1000);
			return () => clearInterval(timer);
		}
	}, [linkUrl]);

	const handleCopy = async () => {
		try {
			await navigator.clipboard.writeText(COMPANY_URL);
			setCopied(true);
		} catch {
			const textArea = document.createElement("textarea");
			textArea.value = COMPANY_URL;
			document.body.appendChild(textArea);
			textArea.select();
			document.execCommand("copy");
			document.body.removeChild(textArea);
			setCopied(true);
		}
	};

	const handleError = () => {
		// If error mentions verification, redirect to user profile
		if (error?.toLowerCase().includes("verification")) {
			return navigate(`/user/${userId}`, { replace: true });
		}
		return navigate(`/user/${userId}`, { replace: true });
	};

	if (error) {
		setTimeout(() => {
			handleError();
		}, 5000);
		return (
			<Box p={4} textAlign="center">
				<Typography color="error" variant="h6">
					{error}
				</Typography>
				<Typography variant="body1" sx={{ mt: 2 }}>
					{error?.toLowerCase().includes("verification") ?
						"Please complete identity verification first."
					:	"Something unexpected happened. Redirecting you to your dashboard shortly…"
					}
				</Typography>
				<Box sx={{ mt: 3 }}>
					<CircularProgress />
				</Box>
			</Box>
		);
	}

	return (
		<Box
			component={motion.div}
			initial={{ opacity: 0 }}
			animate={{ opacity: 1 }}
			exit={{ opacity: 0 }}
			sx={{
				position: "fixed",
				top: 0,
				left: 0,
				width: "100vw",
				height: "100vh",
				bgcolor: "background.paper",
				display: "flex",
				flexDirection: "column",
				alignItems: "center",
				justifyContent: "center",
				p: 2,
				textAlign: "center",
				zIndex: 1300,
			}}>
			<AnimatePresence>
				<motion.div
					key="business"
					initial={{ scale: 0 }}
					animate={{ scale: 1 }}
					transition={{ type: "spring", stiffness: 260, damping: 20 }}>
					<BusinessIcon color="primary" sx={{ fontSize: 80, mb: 2 }} />
				</motion.div>
			</AnimatePresence>

			<Typography variant="h4" gutterBottom>
				Onboarding Your Bank Account
			</Typography>
			<Typography variant="body1" sx={{ mb: 3 }}>
				You’re being redirected to Stripe to finish your bank setup.
			</Typography>
			<Alert severity="info" sx={{ mb: 3, maxWidth: 520 }}>
				Personal bank accounts are fine. Stripe may label some fields as
				“business” — just enter your own details. <br />
				If Stripe asks for a website URL, use:&nbsp;
				<MUILink
					href="https://fundsyfi.com"
					target="_blank"
					rel="noopener noreferrer">
					{COMPANY_URL}
				</MUILink>
				<Tooltip title="Copy URL">
					<IconButton
						size="small"
						onClick={handleCopy}
						sx={{ ml: 1, verticalAlign: "middle" }}>
						<ContentCopyIcon fontSize="inherit" />
					</IconButton>
				</Tooltip>
			</Alert>
			<Snackbar
				open={copied}
				autoHideDuration={2500}
				onClose={() => setCopied(false)}
				message="URL copied to clipboard"
			/>
			<Typography variant="body2">
				If you’re not redirected in{" "}
				<Box component="span" fontWeight="bold">
					{countdown}
				</Box>{" "}
				seconds, click{" "}
				{linkUrl ?
					<Link component={RouterLink} to={linkUrl}>
						here
					</Link>
				:	"—"}
				.
			</Typography>
			<Box sx={{ mt: 3 }}>
				<CircularProgress />
			</Box>
		</Box>
	);
}
